import React, { useMemo } from 'react';
import styled from 'styled-components';
import { darken } from 'polished';

export const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 20px;
  background-color: ${({ theme }) => theme.colors.background};
`;

export const Title = styled.h2`
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 20px;
  font-size: 24px;
`;

export const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  border-radius: 10px;
`;

export const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 10px;
  white-space: nowrap;

  th {
    padding: 15px 10px;
    font-size: 14px;
    font-weight: bold;
    color: ${({ theme }) => theme.colors.primary};
    text-align: left;
    position: sticky;
    top: 0;
    background: white;
    z-index: 1;
  }

  td {
    padding: 15px 10px;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.primary};
  }

  tbody tr {
    border-radius: 8px;
    background-color: #ffffff;
  }

  tbody tr:nth-child(odd) {
    background-color: #e9ecef;
  }
`;

interface ApiDataViewerProps {
  data: any[];
  name?: string;
}

const formatValue = (val: any, col: string) => {
  if (val === null || val === undefined) return '-';
  if (typeof val === 'object') return JSON.stringify(val);
  
  if (col.toLowerCase().includes('valor') && typeof val === 'number') {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  }
  
  if (typeof val === 'string') {
    const dateRegex = /^(\d{4})-(\d{2})-(\d{2})(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
    const match = val.match(dateRegex);
    if (match) {
      return `${match[3]}/${match[2]}/${match[1]}`;
    }
  }
  
  return String(val);
};

const ApiDataViewer: React.FC<ApiDataViewerProps> = ({ data, name }) => {
  const columns = useMemo(() => {
    if (!data || data.length === 0) return [];
    // Extract unique keys from all objects
    const keys = new Set<string>();
    data.forEach((item) => {
      Object.keys(item).forEach((key) => keys.add(key));
    });
    return Array.from(keys);
  }, [data]);

  if (!data || data.length === 0) {
    return (
      <Container>
        <Title>Nenhum dado importado para exibir</Title>
      </Container>
    );
  }

  return (
    <Container>
      {name && <Title>Visualização de Dados: {name}</Title>}
      <TableContainer>
        <StyledTable>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, index) => (
              <tr key={index}>
                {columns.map((col) => (
                  <td key={col}>
                    {formatValue(row[col], col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>
    </Container>
  );
};

export default ApiDataViewer;
